<script lang="ts" setup>
import { ref, computed } from 'vue';
import { BAlert, BButton, BModal, BSpinner } from 'bootstrap-vue-next';
import { useUserStore } from '@/store/user';
import keycloak from '@/auth/keycloak';

const userStore = useUserStore();

// --- Component properties ---
const props = defineProps<{
  show: boolean;
  credentialId: string;
  credentialLabel: string;
  credentialType: string;
}>();

// --- Component emits ---
const emits = defineEmits(['close', 'closeAndRefresh']);

// --- Component state ---
const show = computed<boolean>(() => {
  return props.show;
});

const deletionInProgress = ref(false);
const deletionError = ref<string | null>(null);
const deletionShowSuccess = ref(false);

// --- Handle component actions ---
async function onDeletionConfirmed() {
  if (keycloak.token === null) {
    deletionError.value =
      'Cannot delete credential, authentication failed. Refresh and try again.';
    return;
  }

  deletionInProgress.value = true;
  deletionError.value = null;
  deletionShowSuccess.value = false;

  try {
    deletionShowSuccess.value = await userStore.credentialDelete(
      props.credentialId
    );
  } catch (error: any) {
    deletionError.value = `Delete failed: ${error.message || error}`;
  } finally {
    deletionInProgress.value = false;
  }

  if (deletionShowSuccess.value) {
    setTimeout(() => {
      deletionShowSuccess.value = false;
      emits('closeAndRefresh');
    }, 2000);
  }
}

function handleCloseModal() {
  if (!deletionInProgress.value && !deletionShowSuccess.value) {
    emits('close');
  }
}
</script>
<template>
  <div>
    <!-- MODAL: MFA Credential Delete -->
    <b-modal v-model="show" @hide="handleCloseModal">
      <!-- Title -->
      <template #title>
        <font-awesome-icon icon="trash" class="me-2" />
        Delete
        <strong>{{ props.credentialLabel }}</strong>
      </template>
      <!-- Content -->
      <div>
        <!-- Delete? Confirmation Message -->
        <p>
          Are you sure you want to delete the {{ credentialType }}
          <strong>{{ credentialLabel }}</strong
          >?
        </p>

        <!-- Error Message -->
        <b-alert
          v-model="deletionError"
          variant="danger"
          dismissible
          class="mt-3"
          @dismissed="deletionError = null"
        >
          {{ deletionError }}
        </b-alert>
      </div>
      <!-- Footer -->
      <template #footer>
        <b-button variant="secondary" @click="handleCloseModal">
          Cancel
        </b-button>
        <b-button
          variant="danger"
          :disabled="deletionInProgress || deletionShowSuccess"
          @click="onDeletionConfirmed"
        >
          <span v-if="deletionInProgress">
            Deleting {{ credentialType }}
            <b-spinner small label=""></b-spinner>
          </span>
          <span v-else-if="deletionShowSuccess">
            {{ credentialType }} Deleted
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
          </span>
          <span v-else> Delete {{ credentialType }} </span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
